import { useState } from 'react';
import { Link } from 'react-router';
import { usePendingCheckIns, useReviewCheckIn } from '../../api/queries';
import { QueryBoundary } from '../../components/QueryBoundary';
import { PageHeader } from '../../components/PageHeader';
import { useTranslation } from '../../i18n/I18nProvider';
import { toErrorMessage } from '../../i18n/errorMessage';
import { formatDate } from '../../lib/format';
import { Badge, Button, Card, CardHeader, SkeletonList, Textarea, useToast } from '../../ui';

/**
 * Trenerov red čekanja: samo predani check-inovi, najnoviji prvi.
 * Odgovor se piše na mjestu — otvaranje zasebnog ekrana za tri rečenice
 * je trošak koji trener plaća deset puta dnevno.
 */
export function TrainerCheckIns() {
  const { t, locale } = useTranslation();
  const { toast } = useToast();
  const pending = usePendingCheckIns();
  const review = useReviewCheckIn();

  const [openId, setOpenId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);

  async function onSend(id: string): Promise<void> {
    const response = draft.trim();
    if (!response) {
      setError(t('checkin.responseRequired'));
      return;
    }
    try {
      await review.mutateAsync({ id, response });
      setOpenId(null);
      setDraft('');
      setError(undefined);
      toast(t('checkin.responseSent'), 'success');
    } catch (err) {
      toast(toErrorMessage(t, err).title, 'danger');
    }
  }

  return (
    <>
      <PageHeader eyebrow={t('shell.trainer')} title={t('checkin.pendingTitle')} />

      <QueryBoundary
        query={pending}
        skeleton={<SkeletonList rows={3} />}
        isEmpty={(d) => d.items.length === 0}
        empty={{ message: t('checkin.noPending') }}
      >
        {(data) => (
          <ul className="flex flex-col gap-4">
            {data.items.map((row) => {
              const open = openId === row.id;
              return (
                <li key={row.id}>
                  <Card as="article">
                    <CardHeader
                      eyebrow={t('checkin.week', {
                        date: formatDate(`${row.weekStart}T00:00:00.000Z`, locale),
                      })}
                      title={
                        <Link
                          to={`/trener/klijenti/${row.clientId}`}
                          className="hover:text-accent-dark"
                        >
                          {row.clientName ?? row.clientId}
                        </Link>
                      }
                      action={<Badge tone="info">{t('checkin.statusSubmitted')}</Badge>}
                    />

                    {/*
                      Poruka klijenta stoji IZNAD brojki i u punoj boji teksta.
                      Brojke se skeniraju u sekundi; rečenica „naporan tjedan na
                      poslu" je ono zbog čega trener mijenja sljedeći tjedan, a
                      stajala je sitna i siva ispod pet stupaca.
                    */}
                    {row.comment && (
                      <p className="text-[15px] leading-relaxed text-ink">{row.comment}</p>
                    )}

                    <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-[13px] sm:grid-cols-5">
                      <Stat label={t('checkin.weight')} value={row.weightKg} suffix=" kg" />
                      <Stat label={t('checkin.energy')} value={row.energy} suffix="/5" />
                      <Stat label={t('checkin.sleep')} value={row.sleep} suffix="/5" />
                      <Stat
                        label={t('checkin.nutritionAdherence')}
                        value={row.nutritionAdherence}
                        suffix="%"
                      />
                      <Stat
                        label={t('checkin.trainingAdherence')}
                        value={row.trainingAdherence}
                        suffix="%"
                      />
                    </dl>

                    {open ? (
                      <div className="mt-4 flex flex-col gap-3">
                        <Textarea
                          id={`review-${row.id}`}
                          label={t('checkin.respond')}
                          placeholder={t('checkin.responsePlaceholder')}
                          value={draft}
                          error={error}
                          onChange={(e) => {
                            setDraft(e.target.value);
                            setError(undefined);
                          }}
                        />
                        <div className="flex justify-end gap-3">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                              setOpenId(null);
                              setError(undefined);
                            }}
                          >
                            {t('common.cancel')}
                          </Button>
                          <Button
                            size="sm"
                            loading={review.isPending}
                            onClick={() => void onSend(row.id)}
                          >
                            {t('checkin.respond')}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-4 flex justify-end">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => {
                            setOpenId(row.id);
                            setDraft('');
                            setError(undefined);
                          }}
                        >
                          {t('checkin.respond')}
                        </Button>
                      </div>
                    )}
                  </Card>
                </li>
              );
            })}
          </ul>
        )}
      </QueryBoundary>
    </>
  );
}

function Stat({ label, value, suffix }: { label: string; value: number | null; suffix: string }) {
  return (
    <div>
      <dt className="text-ink-muted">{label}</dt>
      <dd className="text-ink tabular-nums">{value === null ? '—' : `${value}${suffix}`}</dd>
    </div>
  );
}
